import { useEffect, useRef, useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { ConfirmDialog } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { MailThread } from "../../../lib/mail";
import type { CrmLabel } from "../../../lib/crmLabels";
import { useWorkCompact } from "../_shared/density";
import { ThreadHead } from "./head";
import { MessageBubble } from "./MessageBubble";
import { ReplyFoot } from "./ReplyFoot";
import type { TemplateSeed } from "./TemplateMenu";

/**
 * The reading pane.
 *
 * Three parts, and this file owns only the frame around them: `head/` is the
 * subject, the actions and the sender line, the middle is the conversation
 * itself, and the foot is where the answer gets written - the box opens the
 * composer, and the strip under it (`ReplyFoot`) carries the controls that
 * belong to sending rather than to reading.
 */
export function ThreadPane({
  thread,
  loading,
  labels,
  campaignName,
  fromAddress,
  onReply,
  onForward,
  onPatch,
  onDelete,
  onChanged,
  onBack,
}: {
  thread: MailThread | null;
  loading: boolean;
  /** The label registry, for the chips under the subject and the menu toggles. */
  labels: CrmLabel[];
  campaignName?: string;
  /** The mailbox a reply leaves from, shown in the footer. */
  fromAddress: string;
  /** A seed prefills the composer - that is how a template gets into a reply. */
  onReply: (seed?: TemplateSeed) => void;
  onForward: () => void;
  onPatch: (body: Record<string, unknown>) => Promise<void>;
  onDelete: () => void;
  onChanged: () => void;
  /** Back to the conversation list. A phone shows one pane at a time. */
  onBack: () => void;
}) {
  const t = useT();
  const compact = useWorkCompact();
  const [confirmDel, setConfirmDel] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // A different conversation starts at the top, not wherever the last one was
  // left - the scroll box is reused across threads.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [thread?.id]);

  if (loading) {
    return (
      <div className="crm-card crm-work-col items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="crm-card crm-work-col">
        <div className="crm-pane-none">
          <Mail className="size-8 opacity-50" />
          <span>{t("mail.pickOne")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="crm-card crm-work-col">
      <ThreadHead
        thread={thread}
        labels={labels}
        campaignName={campaignName}
        onReply={onReply}
        onForward={onForward}
        onPatch={onPatch}
        onDelete={() => setConfirmDel(true)}
        onChanged={onChanged}
        onBack={onBack}
      />

      <div ref={bodyRef} className="crm-read-body">
        <div className={compact ? "space-y-2" : "space-y-3"}>
          {(thread.messages ?? []).map((m) => (
            <MessageBubble key={m.id} t={t} m={m} />
          ))}
        </div>
      </div>

      <div className="crm-read-foot">
        <button type="button" className="crm-reply-box" onClick={() => onReply()}>
          {t("mail.replyPlaceholder")}
        </button>
        <ReplyFoot onReply={() => onReply()} onTemplate={onReply} fromAddress={fromAddress} />
      </div>

      <ConfirmDialog
        open={confirmDel}
        title={t("email.deleteTitle")}
        description={t("email.deleteConfirm")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        variant="destructive"
        onConfirm={() => {
          setConfirmDel(false);
          onDelete();
        }}
        onCancel={() => setConfirmDel(false)}
      />
    </div>
  );
}
